<script setup lang="ts">
import type { Rules } from "async-validator";
import type { Ref } from "vue";
import { ErrorContext } from "~/core/models/ErrorContext.enum";
import LxcError from "~/core/models/LxcError.class";
import { getEntityAdminValidationRules } from "~/core/utils/entityAdminValidationRules";
import { getEntityLocationValidationRules } from "~/core/utils/entityLocationValidationRules";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { typeOptions } from "~/modules/organization/constants";
import type { OrganizationCreate } from "~/modules/organization/models/OrganizationCreate.type";
import organizationService from "~/modules/organization/services/organization.service";

const { t } = useI18n();

const props = defineProps<{
  show: boolean;
  businessUnitCode: number;
}>();

const emit = defineEmits(["update:show", "create:success"]);

const showCreateCanvas: WritableComputedRef<boolean> = computed({
  get: () => {
    return props.show;
  },
  set: (shown: boolean) => {
    emit("update:show", shown);
  },
});

const organizationForm: Partial<OrganizationCreate> = reactive({
  name: "",
  type: undefined,
  country: "",
  timezone: "",
  administrator: {
    email: "",
    firstName: "",
    lastName: "",
    language: "",
    login: "",
  },
});
const organizationFormRef: Ref<HTMLFormElement | undefined | null> = ref();
const formRules: Rules = {
  ...getEntityLocationValidationRules(t),
  ...getEntityAdminValidationRules(t),
  name: [
    {
      type: "string",
      required: true,
      message: t("administration.organization.nameRequiredError"),
    },
    {
      type: "string",
      max: 60,
      message: t("administration.organization.nameLengthError"),
    },
  ],
  type: [
    {
      type: "string",
      required: true,
      message: t("administration.organization.typeError"),
    },
  ],
};
const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => organizationFormRef.value?.isValid,
);
const isLoading: Ref<boolean> = ref(false);

function onClose() {
  showCreateCanvas.value = false;
  clearForm();
}

async function onSubmit() {
  if (isFormValid.value) {
    isLoading.value = true;
    const organization: OrganizationCreate = {
      name: organizationForm.name!,
      type: organizationForm.type!,
      country: organizationForm.country!,
      timezone: organizationForm.timezone!,
      administrator: {
        email: organizationForm.administrator!.email!,
        firstName: organizationForm.administrator!.firstName!,
        lastName: organizationForm.administrator!.lastName!,
        language: organizationForm.administrator!.language!,
        login: organizationForm.administrator!.login!,
      },
    };
    const response = await organizationService.createOrganization(
      props.businessUnitCode,
      organization,
    );
    if (LxcError.check(response)) {
      (response as LxcError).notify(
        NotificationKey.saveError,
        ErrorContext.ORGANIZATION,
      );
    } else {
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      emit("create:success", response);
      onClose();
    }
    isLoading.value = false;
  }
}

function clearForm() {
  setTimeout(() => {
    organizationFormRef.value?.clearValidate();
    organizationForm.name = "";
    organizationForm.type = undefined;
    organizationForm.country = "";
    organizationForm.timezone = "";
    if (organizationForm.administrator) {
      organizationForm.administrator.email = "";
      organizationForm.administrator.firstName = "";
      organizationForm.administrator.lastName = "";
      organizationForm.administrator.language = "";
      organizationForm.administrator.login = "";
    }
  }, 1000);
}

onMounted(clearForm);
</script>

<template>
  <lxc-side-canvas
    v-model:show="showCreateCanvas"
    :header="t('administration.organization.create.title')"
    size="1/2"
    @hidden="onClose"
  >
    <lxc-form
      ref="organizationFormRef"
      :rules="formRules"
      :model="organizationForm"
      @submit.prevent="onSubmit"
    >
      <h3 class="mb-6">
        {{ t("administration.organization.information.title") }}
      </h3>
      <div class="grid gap-x-8 grid-cols-2">
        <lxc-form-item
          :label="t('administration.organization.name')"
          prop="name"
          class="basis-1/2"
        >
          <lxc-input v-model="organizationForm.name" type="text" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('administration.organization.type.label')"
          prop="type"
          class="basis-1/2"
        >
          <lxc-select
            v-model="organizationForm.type"
            :placeholder="t('administration.organization.type.placeholder')"
          >
            <lxc-option
              v-for="organizationType in typeOptions"
              :key="organizationType.key"
              :value="organizationType.key"
            >
              {{ t(organizationType.labelKey) }}
            </lxc-option>
          </lxc-select>
        </lxc-form-item>
        <lxc-form-item
          :label="t('administration.entityLocation.timezone')"
          prop="timezone"
          class="basis-1/2"
        >
          <timezone-component v-model="organizationForm.timezone" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('administration.entityLocation.country')"
          prop="country"
          class="basis-1/2"
        >
          <country-component v-model="organizationForm.country" />
        </lxc-form-item>
      </div>
      <entity-administrator v-model="organizationForm.administrator!" />
    </lxc-form>
    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="onClose"
        />
        <lxc-button
          html-type="button"
          type="primary"
          class="grow"
          :text="t('administration.organization.create.saveButton')"
          :title="t('administration.organization.create.saveButton')"
          :is-loading="isLoading"
          :disabled="!isFormValid"
          @click="onSubmit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
